import Link, { LinkProps } from 'next/link';
import { useRouter } from 'next/navigation';
import React, { HTMLAttributeAnchorTarget, ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';

interface IListItemChildrenProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}
function ListItemLeft({ children, className }: IListItemChildrenProps) {
  return <div className={twMerge('flex-auto flex items-center gap-2', className)}>{children}</div>;
}
function ListItemRight({ children, className }: IListItemChildrenProps) {
  return (
    <div className={twMerge('flex-none ml-auto flex items-center gap-2', className)}>
      {children}
    </div>
  );
}

interface IListItemProps extends Partial<Pick<LinkProps, 'href' | 'onClick'>> {
  className?: string;
  icon?: React.ReactNode;
  children?: ReactElement<IListItemChildrenProps> | Array<ReactElement<IListItemChildrenProps>>;
  target?: HTMLAttributeAnchorTarget;
}

interface IListItemWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * A generic list item component that can be used to display a list item.
 *
 * It takes an optional icon and children as props. The children will be rendered
 * to the left of the icon, and the icon will be rendered to the left of the
 * children.
 *
 * The component also accepts a className prop, which can be used to override
 * the default styles.
 *
 * @param {React.ReactNode} icon - An optional icon to render to the left of the children.
 * @param {ReactElement<IListItemChildrenProps>} children - The children to render to the right of the icon.
 * @param {string} className - An optional className to override the default styles.
 */
function ListItem({ icon, children, className, ...props }: IListItemProps) {
  const router = useRouter();
  const handlePress = (event: any) => {
    if (props.onClick) props.onClick?.(event);
  };

  const ListItemWrapper = ({ children }: IListItemWrapperProps) => {
    return (
      <>
        {props.href ? (
          <Link
            href={props.href as string}
            target={props.target as HTMLAttributeAnchorTarget}
            className={twMerge(
              'flex items-center gap-2 p-4 transition-all duration-200 ease-in-out hover:bg-darkglass',
              className
            )}
          >
            {children}
          </Link>
        ) : (
          <div
            onClick={handlePress}
            className={twMerge(
              'flex items-center gap-2 p-4 transition-all duration-200 ease-in-out hover:bg-darkglass',
              className,
              props.href && 'cursor-pointer'
            )}
          >
            {children}
          </div>
        )}
      </>
    );
  };

  return (
    <ListItemWrapper>
      {icon && <span className="flex-none flex justify-center self-center">{icon}</span>}
      {children}
    </ListItemWrapper>
  );
}

ListItem.Left = ListItemLeft;
ListItem.Right = ListItemRight;

export default ListItem;
